import { makeConsent, showConsent } from "@flow/common/consent_logic";
import { Consent, ConsentConfig, ConsentLevel } from "@flow/common/types";
import { ConsentInputComponent } from "@flow/frontend/common/ConsentInputComponent";
import { Divider } from "@mui/material";
import * as React from "react";

interface ConsentInputComponentBlockProps {
  consentConfigs: ConsentConfig[];
  alreadyConsentedTo: Consent[] | undefined;
  updateConsents: (consents: Consent[]) => void;
  displayTopBorder?: boolean;
  displayBottomBorder?: boolean;
}

/**
 * Given an array of ConsentConfigs return a matching set of Consents which are initialised according to the
 * assumed default ConsentLevel - e.g. checkbox's start off unchecked, etc etc.
 */
export function getDefaultConsentsFromConfigs(configs: ConsentConfig[]): Consent[] {
  return configs.map((c) => makeConsent(consentConfigDefaultLevel(c), c));
}
function consentConfigDefaultLevel(c: ConsentConfig): ConsentLevel {
  switch (c.input.type) {
    case "implicit":
      return "implicit";
    // SW-200 - New consent method type.
    case "onfieldpresent":
      return "explicit_opt_in";
    case "checkbox":
      return c.input.mapping[0];
    case "radiobuttons":
      return c.input.no_consent_level;
  }
}

/**
 * ConsentInputComponentBlock  renders a list of ConsentInputComponent
 * elements based on the provided consents. It filters the consents based on whether they should
 * be shown or not, using the alreadyConsentedTo array and showConsent function.
 *
 * @param {ConsentInputComponentBlockProps} props - The properties for the component, including:
 *   - consentConfigs: An array of ConsentConfig objects to display as ConsentInputComponent elements.
 *   - alreadyConsentedTo: An array containing consent GUIDs the user has already consented to.
 *   - updateConsents: A callback function called when the consents that this Block manages are modified -
 *                     normally in response to user interactions
 *   - sx: Optional block of Material UI format styling, to be applied to the rendered ConsentInputComponent elements.
 *
 * @returns A fragment containing a list of ConsentInputComponent elements.
 */
export function ConsentInputComponentBlock(props: ConsentInputComponentBlockProps) {
  // On initial load, and if our alreadyConsentedTo list is changed, set consents to defaults.
  React.useEffect(() => {
    props.updateConsents(
      getDefaultConsentsFromConfigs(props.consentConfigs.filter((c) => showConsent(props.alreadyConsentedTo, c)))
    );
  }, [props.alreadyConsentedTo]);

  const consentsToRender = props.consentConfigs.filter((c) => showConsent(props.alreadyConsentedTo, c));

  if (consentsToRender.length === 0) {
    return null;
  }

  return (
    <>
      {props.displayTopBorder && <Divider />}
      {consentsToRender.map((config, idx, array) => (
        <ConsentInputComponent
          key={config.guid}
          inputName={idx.toString()}
          updateConsent={(c) => {
            props.updateConsents([c]);
          }}
          config={config}
          sx={{ marginBottom: idx === array.length - 1 ? "0" : "0.75rem" }} // no bottom margin on last element
        />
      ))}
      {props.displayBottomBorder && <Divider sx={{ marginBottom: "0" }} />}
    </>
  );
}
